import HTTP from '../libs/HTTP';

export interface ProjectQueryParam {
    id?: number;
    name?: string;
    companyId?: number;
    customerManagerId?: number;
    projectManagerAdminId?: number;
    salesAdminId?: number;
    serviceCategory?: string;
    statusList?: number[];
    beginDate?: string;
    endDate?: string;
    includeDeleted?: boolean;
}

export interface ProjectListRequest {
    queryParam: ProjectQueryParam;
    pageNo: number;
    pageSize: number;
}

export interface IProjectStage {
    id?: number;
    projectId?: number;
    stageNo?: number;
    stageName?: string;
    implementationTime?: string;
    implementationContent?: string;
    executorType?: number;
    executorId?: number;
    executorName?: string;
    executorDisplayName?: string;
    deliverable?: string;
    paymentPlan?: string;
    status?: number;
    sortNo?: number;
}

export interface IProjectAttachment {
    id: number;
    projectId: number;
    stageId?: number;
    stageName?: string;
    attachmentType: number;
    fileName: string;
    fileUrl: string;
    fileExt?: string;
    fileSize?: number;
    createTime?: number;
}

export interface IProjectInfo {
    id: number;
    name: string;
    startDate?: number;
    endDate?: number;
    serviceCategory?: string;
    companyId: number;
    companyName?: string;
    customerManagerId: number;
    customerManagerName?: string;
    projectManagerAdminId?: number;
    projectManagerName?: string;
    salesAdminId?: number;
    salesName?: string;
    contractAmount?: number;
    status: number;
    actualIncome?: number;
    costConsultantFee?: number;
    costConsultantTravel?: number;
    costConsultantMeal?: number;
    costMaterial?: number;
    costTeachingAid?: number;
    costTeaBreak?: number;
    costPrize?: number;
    costPmTravel?: number;
    costVat?: number;
    costOtherA?: number;
    costOtherB?: number;
    costOtherC?: number;
    otherCostItems?: string | null;
    incomeMismatchReason?: string | null;
    incomeMismatchRemark?: string | null;
    projectProfit?: number;
    profitRate?: number;
    projectTarget?: string;
    onsiteFeedbackStudent?: string;
    onsiteFeedbackClient?: string;
    resultFeedbackSponsor?: string;
    resultTeamChange?: string;
    resultBusinessChange?: string;
    states?: number;
    createTime?: number;
    lastEditTime?: number;
    stageList?: IProjectStage[];
    attachmentList?: IProjectAttachment[];
}

export interface IProjectInfoList {
    records: IProjectInfo[];
    total: number;
    size: number;
    current: number;
    pages: number;
}

export interface ProjectSaveParam {
    project: Partial<IProjectInfo>;
    stageList?: IProjectStage[];
}

export interface ProjectUpdateParam extends Partial<IProjectInfo> {
    id: number;
}

export interface IProjectBaseOption {
    id: number;
    name: string;
}

class ProjectService extends HTTP {
    async getProjectList(request: ProjectListRequest) {
        const response = await this.post('/project/getProject', request.queryParam, {
            params: {
                pageNo: request.pageNo,
                pageSize: request.pageSize,
            },
        });
        return response ? response.data : null;
    }

    async getMyProjectList(request: ProjectListRequest) {
        const response = await this.post('/project/getMyProject', request.queryParam, {
            params: {
                pageNo: request.pageNo,
                pageSize: request.pageSize,
            },
        });
        return response ? response.data : null;
    }

    async getProjectById(id: number) {
        const response = await this.get(`/project/getProjectById/${id}`);
        return response ? response.data : null;
    }

    async getStageExecutorOptions() {
        const response = await this.get('/project/getStageExecutorOptions');
        return response ? response.data : null;
    }

    async getProjectBaseOptions() {
        const response = await this.get('/project/getProjectBaseOptions');
        return response ? response.data : null;
    }

    async addProject(request: ProjectSaveParam) {
        const response = await this.post('/project/addProject', request);
        return response ? response.data : null;
    }

    async updateProject(request: ProjectUpdateParam) {
        const response = await this.put(`/project/updateProject/${request.id}`, request);
        return response ? response.data : null;
    }

    async deleteProject(id: number) {
        const response = await this.delete(`/project/deleteProject/${id}`);
        return response ? response.data : null;
    }

    async addStage(request: IProjectStage) {
        const response = await this.post('/project/addStage', request);
        return response ? response.data : null;
    }

    async updateStage(stageId: number, request: IProjectStage) {
        const response = await this.put(`/project/updateStage/${stageId}`, request);
        return response ? response.data : null;
    }

    async deleteStage(stageId: number) {
        const response = await this.delete(`/project/deleteStage/${stageId}`);
        return response ? response.data : null;
    }

    async uploadAttachment(projectId: number, file: File, stageId?: number, attachmentType?: number) {
        const formData = new FormData();
        formData.append('file', file);
        if (stageId !== undefined && stageId !== null) {
            formData.append('stageId', stageId.toString());
        }
        if (attachmentType !== undefined && attachmentType !== null) {
            formData.append('attachmentType', attachmentType.toString());
        }
        const response = await this.post(`/project/uploadAttachment/${projectId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response ? response.data : null;
    }

    async deleteAttachment(attachmentId: number) {
        const response = await this.delete(`/project/deleteAttachment/${attachmentId}`);
        return response ? response.data : null;
    }

    async exportProject(request: ProjectListRequest) {
        const response = await this.post('/project/exportProject', request.queryParam, {
            responseType: 'blob',
        } as any);
        return response || null;
    }
}

export default new ProjectService();
